import { useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import api from '../../../api/api';
import useAdminDraftGuard from '../useAdminDraftGuard';
import SupportPromotionPreview, { promotionCopies as copies } from './SupportPromotionPreview';

const endpoint = '/admin/app/support-promotion';
const surfaces = [
  [
    'HOME',
    '홈',
    [
      ['discovery', '새로운 발견 뒤'],
      ['reviewers', '회원 둘러보기 뒤'],
    ],
  ],
  [
    'EXPLORE',
    '탐색',
    [
      ['latest', '최신 해외 앨범 뒤'],
      ['reviews', '최근 리뷰 뒤'],
      ['playlists', '회원들의 플레이리스트 뒤'],
    ],
  ],
  [
    'SEARCH',
    '검색 홈',
    [
      ['taste', '취향으로 발견하기 뒤'],
      ['reviews', '리뷰에서 발견하기 뒤'],
      ['playlists', '플리로 발견하기 뒤'],
    ],
  ],
];
export default function SupportPromotionManagement() {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [reload, setReload] = useState(0);
  const [previewDark, setPreviewDark] = useState(false);
  const [previewEnglish, setPreviewEnglish] = useState(false);
  const [previewSupporter, setPreviewSupporter] = useState(false);
  const draft = useAdminDraftGuard(form, { enabled: form !== null, busy });
  useEffect(() => {
    let live = true;
    setForm(null);
    api
      .getAuth(endpoint)
      .then(({ data }) => {
        if (live) {
          setForm({ myEnabled: true, myVariant: 1, ...data.data });
          setError('');
        }
      })
      .catch(() => {
        if (live) setError('설정을 불러오지 못했습니다.');
      });
    return () => {
      live = false;
    };
  }, [reload]);
  const change = (surface, value) => {
    setSaved(false);
    setForm((current) => ({
      ...current,
      placements: [
        ...current.placements.filter((p) => p.surface !== surface),
        ...(value ? [value] : []),
      ],
    }));
  };
  const save = async () => {
    setBusy(true);
    setError('');
    setSaved(false);
    try {
      const { data } = await api.putAuth(endpoint, form);
      setForm(data.data);
      draft.markClean(data.data);
      void queryClient.invalidateQueries({ queryKey: ['support-promotion'] });
      setSaved(true);
    } catch (failure) {
      setError(failure.response?.data?.msg || '저장하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  };
  if (!form)
    return error ? (
      <Alert
        severity="error"
        action={<Button onClick={() => setReload((x) => x + 1)}>재시도</Button>}
      >
        {error}
      </Alert>
    ) : (
      <Typography>불러오는 중…</Typography>
    );
  return (
    <Stack spacing={2}>
      <Typography variant="h6">후원 안내 배치</Typography>
      <Alert severity="info">
        화면별 한 위치를 선택합니다. 선택한 섹션이 없으면 해당 안내도 생략합니다. 설정한 위치마다
        독립적으로 표시하며, 이전에 안내를 본 기록이나 숨긴 기록으로 표시를 제한하지 않습니다. MY는
        아래 별도 설정을 따릅니다. 결제를 활성화하는 설정은 아닙니다.
      </Alert>
      <Typography variant="body2">
        미리보기는 노출 제한과 관계없이 확인할 수 있으며, 설정을 저장하거나 실제 노출 횟수를
        사용하지 않습니다. 긴 문구·큰 글자는 카드가 높아질 수 있습니다.
      </Typography>
      <Stack direction="row" flexWrap="wrap">
        <FormControlLabel
          label="다크 미리보기"
          control={
            <Checkbox checked={previewDark} onChange={(e) => setPreviewDark(e.target.checked)} />
          }
        />
        <FormControlLabel
          label="영어 미리보기"
          control={
            <Checkbox
              checked={previewEnglish}
              onChange={(e) => setPreviewEnglish(e.target.checked)}
            />
          }
        />
        <FormControlLabel
          label="후원자 미리보기"
          control={
            <Checkbox
              checked={previewSupporter}
              onChange={(e) => setPreviewSupporter(e.target.checked)}
            />
          }
        />
      </Stack>
      {error && <Alert severity="error">{error}</Alert>}
      <Stack component="fieldset" disabled={busy} spacing={2} sx={{ border: 0, p: 0, m: 0 }}>
        <FormControlLabel
          label="인라인 후원 안내 사용"
          control={
            <Checkbox
              checked={form.enabled}
              onChange={(e) => {
                setSaved(false);
                setForm({ ...form, enabled: e.target.checked });
              }}
            />
          }
        />
        {surfaces.map(([surface, label, slots]) => {
          const item = form.placements.find((p) => p.surface === surface);
          return (
            <Paper variant="outlined" key={surface} sx={{ p: 2 }}>
              <Stack spacing={2}>
                <TextField
                  select
                  label={`${label} 위치`}
                  value={item?.afterSection || ''}
                  onChange={(e) =>
                    change(
                      surface,
                      e.target.value
                        ? { surface, afterSection: e.target.value, variant: item?.variant || 1 }
                        : null,
                    )
                  }
                >
                  <MenuItem value="">표시 안 함</MenuItem>
                  {slots.map(([key, text]) => (
                    <MenuItem key={key} value={key}>
                      {text}
                    </MenuItem>
                  ))}
                </TextField>
                {item && (
                  <>
                    <TextField
                      select
                      label={`${label} 문구`}
                      value={item.variant}
                      onChange={(e) =>
                        change(surface, { ...item, variant: Number(e.target.value) })
                      }
                    >
                      {copies.map(([title], index) => (
                        <MenuItem key={title} value={index + 1}>
                          {title}
                        </MenuItem>
                      ))}
                    </TextField>
                    <SupportPromotionPreview
                      variant={item.variant}
                      dark={previewDark}
                      english={previewEnglish}
                      supporter={previewSupporter}
                    />
                  </>
                )}
              </Stack>
            </Paper>
          );
        })}
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Typography fontWeight={600}>MY · 나의 최근 활동과 활동 배지 사이</Typography>
            <Typography variant="body2">
              로그인한 회원은 나의 최근 활동과 활동 배지 사이에 표시합니다. 인라인 사용 설정과
              별도로 관리합니다. 이 설정은 새 앱 빌드부터 적용됩니다.
            </Typography>
            <FormControlLabel
              label="MY 후원 안내 표시"
              control={
                <Checkbox
                  checked={form.myEnabled ?? true}
                  onChange={(e) => {
                    setSaved(false);
                    setForm({ ...form, myEnabled: e.target.checked });
                  }}
                />
              }
            />
            <TextField
              select
              label="MY 문구"
              value={form.myVariant ?? 1}
              onChange={(e) => {
                setSaved(false);
                setForm({ ...form, myVariant: Number(e.target.value) });
              }}
            >
              {copies.map(([title], i) => (
                <MenuItem key={title} value={i + 1}>
                  {title}
                </MenuItem>
              ))}
            </TextField>
            <SupportPromotionPreview
              variant={form.myVariant ?? 1}
              permanent
              dark={previewDark}
              english={previewEnglish}
              supporter={previewSupporter}
            />
          </Stack>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography fontWeight={600}>함께해주셔서 감사합니다</Typography>
          <Typography>혜택 알아보기</Typography>
          <Typography variant="caption">
            검증된 후원자에게는 선택 문구 대신 이 감사 인사를 표시합니다. 관리자 권한만으로 후원자로
            판단하지 않습니다.
          </Typography>
        </Paper>
        <Button variant="contained" onClick={save}>
          저장
        </Button>
      </Stack>
      {saved && <Alert severity="success">저장했습니다. 앱 설정 재조회 시 반영됩니다.</Alert>}
    </Stack>
  );
}
